import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUp, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { LogoMark, LogoFull } from "./Logo";
import { BRAND, NAV_LINKS, scrollToId } from "@/lib/content";
import { useExtras } from "@/lib/texts";

export const Footer = () => {
    const footerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: footerRef,
        offset: ["start end", "end end"],
    });
    const logoRotate = useTransform(scrollYProgress, [0, 1], [-180, 0]);
    const logoScale = useTransform(scrollYProgress, [0, 1], [0.6, 1]);
    const logoY = useTransform(scrollYProgress, [0, 1], [80, 0]);
    const logoOpacity = useTransform(scrollYProgress, [0, 0.4, 1], [0, 0.5, 1]);
    const flagScaleX = useTransform(scrollYProgress, [0.35, 1], [0.2, 1]);
    const flagOpacity = useTransform(scrollYProgress, [0.35, 0.8], [0, 1]);
    const { instagram } = useExtras();

    return (
    <footer ref={footerRef} data-testid="footer" className="relative overflow-hidden bg-[#1C1917] text-stone-100">
        <div className="grain pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 pb-10 pt-16 sm:px-8 lg:px-16">
            <div className="flex flex-wrap items-start justify-between gap-10 border-b border-stone-100/10 pb-12">
                <div className="max-w-sm">
                    <span className="flex items-center gap-3">
                        <LogoFull dark className="h-10 w-auto" />
                        <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#E75C15]">
                            Impresa Edile · dal {BRAND.since}
                        </span>
                    </span>
                    <p className="mt-5 text-sm leading-relaxed text-stone-400">
                        Ristrutturazioni, recupero e nuove costruzioni per residenze private,
                        immobili di pregio e strutture ricettive. Un unico interlocutore, dalla
                        gestione del cantiere alla consegna.
                    </p>
                </div>

                <nav className="flex flex-col gap-3">
                    <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-stone-500">
                        Navigazione
                    </span>
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`footer-link-${l.id}`}
                            onClick={() => scrollToId(l.id)}
                            className="text-left text-sm text-stone-300 transition-colors hover:text-[#E75C15]"
                        >
                            {l.label}
                        </button>
                    ))}
                    <Link
                        to="/servizi"
                        data-testid="footer-link-pagina-servizi"
                        className="text-left text-sm text-stone-300 transition-colors hover:text-[#E75C15]"
                    >
                        I Nostri Servizi
                    </Link>
                </nav>

                <div className="flex flex-col gap-3">
                    <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-stone-500">
                        Contatti
                    </span>
                    <a href={`mailto:${BRAND.email}`} className="text-sm text-stone-300 transition-colors hover:text-[#E75C15]">
                        {BRAND.email}
                    </a>
                    <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="text-sm text-stone-300 transition-colors hover:text-[#E75C15]">
                        {BRAND.phone}
                    </a>
                    {instagram && (
                        <a
                            href={instagram}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-instagram-link"
                            className="inline-flex items-center gap-2 text-sm text-stone-300 transition-colors hover:text-[#E75C15]"
                        >
                            <Instagram className="h-4 w-4 text-[#E75C15]" />
                            Seguici su Instagram
                        </a>
                    )}
                    <span className="text-sm text-stone-400">{BRAND.address}</span>
                    <span className="text-sm text-stone-400">{BRAND.piva}</span>
                    <Link
                        to="/admin"
                        data-testid="footer-link-admin"
                        className="mt-2 inline-flex items-center gap-2 text-xs text-stone-500 transition-colors hover:text-[#E75C15]"
                    >
                        Area Riservata →
                    </Link>
                </div>

                <button
                    data-testid="footer-back-to-top"
                    onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-[#E75C15]/40 text-[#E75C15] transition-colors hover:bg-[#E75C15] hover:text-[#1C1917]"
                    aria-label="Torna su"
                >
                    <ArrowUp className="h-5 w-5" />
                </button>
            </div>

            <div className="mt-12 flex flex-col items-center py-6" data-testid="footer-logo-scroll">
                <motion.div
                    style={{
                        rotate: logoRotate,
                        scale: logoScale,
                        y: logoY,
                        opacity: logoOpacity,
                    }}
                >
                    <LogoMark className="h-36 w-auto sm:h-48" />
                </motion.div>
                <motion.img
                    src="/logo-tricolore.png"
                    alt=""
                    aria-hidden="true"
                    data-testid="footer-tricolore"
                    className="mt-4 h-8 w-auto sm:h-10"
                    style={{ scaleX: flagScaleX, opacity: flagOpacity }}
                />
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-stone-100/10 pt-6">
                <span className="text-xs text-stone-500">
                    © {new Date().getFullYear()} {BRAND.legal}. Tutti i diritti riservati.
                </span>
                <span className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-stone-500">
                    Tradizione · Precisione · Eccellenza
                </span>
            </div>
        </div>
    </footer>
    );
};
